import { useState, useEffect, useContext, useRef, useCallback } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import * as Regex from 'utils/validations';
import { DataContext } from 'providers/data';
import { LoadConfig } from 'utils/loadConfig';
import { ParamsPageWebView } from 'utils/typeServices';

import { Typography, Box, Checkbox, Button, Tooltip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';

import Modals from 'components/views/Modals';
import Input from 'components/views/form/Input';
import Select from 'components/views/form/Select';
import ContentForgot from 'components/views/ContentForgot';
import { ButtonRectangle } from 'components/views/form/Button';
import ModalAlertSecurity from 'components/views/ModalAlertSecurity';

import BackgroundLogin from 'assets/images/background-login.png';
import IconPdfBlockedModal from 'assets/icons/ic-pdf-eye-modal.svg';
import IconPdfBlocked from 'assets/icons/ic-pdf-eye.svg';
import IconPdfBlockedTooltip from 'assets/icons/ic-pdf-eye-tooltip.svg';


const dataListMotive = [
    {
        text: "Recebi após o vencimento",
        value: '990',
    },
    {
        text: "Perdi ou foi danificado",
        value: '991',
    },
    {
        text: "Não recebi",
        value: '992',
    },
    {
        text: "Prefiro retirar pelo site",
        value: '993',
    },
    {
        text: "Atualização de boleto vencido",
        value: '1430',
    },
    {
        text: "Pagar com cartão de crédito",
        value: '1431',
    },
];

const Charges = ({ isWebView }: ParamsPageWebView) => {
    const captcha = useRef<ReCAPTCHA>(null);
    const navigate = useNavigate();


    const [carNew, setCarNew] = useState(false);
    const [enableBtn, setEnableBtn] = useState(true);
    const [showError, setShowError] = useState(false);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [keyReCaptcha, setKeyReCaptcha] = useState('');
    const [motiveSelected, setMotiveSelected] = useState({ text: '', value: '' });

    // Estados para o termo de adesão
    const [showTermo, setShowTermo] = useState(false);
    const [termoData, setTermoData] = useState<{ TermoIturanHub: any; TermoIturanHubBase64: string } | null>(null);
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const [termoAccepted, setTermoAccepted] = useState(false);
    const [loadingAcceptTerm, setLoadingAcceptTerm] = useState(false);
    const [showVeiculoConectadoModal, setShowVeiculoConectadoModal] = useState(false);
    const [termoTimer, setTermoTimer] = useState(0);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const [showPdfBlockedModal, setShowPdfBlockedModal] = useState(false);
    const [pdfLoadError, setPdfLoadError] = useState(false);
    const [pdfLoadChecked, setPdfLoadChecked] = useState(false);
    

    const {
        handlePostFinancialPending,
        dataFinancialPending,
        handleAcceptTermo,
        handlePostAcceptTerm,
        handleGetActiveLoginBanner
    } = useContext(DataContext);

    useEffect(() => {
        handleGetActiveLoginBanner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { register, watch } = useForm();

    const identity = watch('identity');
    const chassisOrBoard = watch('chassisOrBoard');

    const handlePostFinancialPendingState = () => {
        setLoadingBtn(true);
        handlePostFinancialPending({
            token: null,
            body: {
                CPF_CNPJ: identity,
                ID_MOTIVO: parseFloat(motiveSelected.value),
                DS_PLACA: chassisOrBoard,
                CD_PARCEIRO: 1783625,
                ZERO_KM: carNew,
                TokenRecaptcha: keyReCaptcha,
            }
        });
    }


    // Converter base64 para Blob URL
    useEffect(() => {
        if (showTermo && termoData?.TermoIturanHubBase64) {
            try {
                const pdfViewerEnabled = (navigator as any).pdfViewerEnabled;
                
                if (typeof pdfViewerEnabled === 'boolean' && pdfViewerEnabled === false) {
                    setPdfLoadError(true);
                    setShowPdfBlockedModal(true);
                    setPdfLoadChecked(true);
                    setPdfBlobUrl(null);
                    return;
                }
                
                // Decodifica o base64
                const binaryString = window.atob(termoData.TermoIturanHubBase64);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                
                // Cria um blob do tipo PDF
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfBlobUrl(url);
                setPdfLoadError(false);
                setShowPdfBlockedModal(false);
                setPdfLoadChecked(false);

                // Cleanup: revoga a URL quando o componente desmontar
                return () => {
                    URL.revokeObjectURL(url);
                    setPdfBlobUrl(null);
                };
            } catch (error) {
                console.error('Erro ao processar PDF:', error);
                setPdfLoadError(true);
                setShowPdfBlockedModal(true);
                setPdfBlobUrl(null);
            }
        }
    }, [showTermo, termoData]);

    // Timer simples para habilitar botões após tempo de leitura
    useEffect(() => {
        if (showTermo && termoData) {
            setHasScrolledToEnd(false);
            setTermoTimer(0);

            // Simula tempo de leitura - habilita botões após 10 segundos
            const timer = setInterval(() => {
                setTermoTimer(prev => {
                    const newTime = prev + 1;
                    if (newTime >= 10) {
                        setHasScrolledToEnd(true);
                        clearInterval(timer);
                    }
                    return newTime;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [showTermo, termoData]);

    // Função para aceitar o termo
    const handleAcceptTermoLocal = async () => {
        if (!termoData?.TermoIturanHub) return;

        setLoadingAcceptTerm(true);

        try {
            await handlePostAcceptTerm({
                token: null,
                body: {
                    CdPerson: termoData.TermoIturanHub.CdPessoa,
                    Accept: true,
                    TypeContractModel: termoData.TermoIturanHub.NrTipoModeloContrato,
                    CdContractModel: termoData.TermoIturanHub.CdModeloContrato
                }
            });

            setTermoAccepted(true);
            setShowTermo(false);
            handleAcceptTermo();
            navigate('pendentes');
        } catch (error: any) {
            if (error?.code === 'REJECT_TERM_FLOW') {
                await handleRejectTermo();
            } else {
                console.error('Erro ao aceitar termo:', error);
            }
        } finally {
            setLoadingAcceptTerm(false);
        }
    };

    // Função para rejeitar o termo
    const handleRejectTermo = async () => {
        if (!termoData?.TermoIturanHub) {
            navigate(-1);
            return;
        }

        setLoadingAcceptTerm(true);

        try {
            await handlePostAcceptTerm({
                token: null,
                body: {
                    CdPerson: termoData.TermoIturanHub.CdPessoa,
                    Accept: false,
                    TypeContractModel: termoData.TermoIturanHub.NrTipoModeloContrato,
                    CdContractModel: termoData.TermoIturanHub.CdModeloContrato
                }
            });

            navigate(-1);
        } catch (error) {
            console.error('Erro ao rejeitar termo:', error);
            navigate(-1);
        } finally {
            setLoadingAcceptTerm(false);
        }
    };

    // Validação dos campos para habilitar/desabilitar o botão
    useEffect(() => {
        if (Regex.matchesIdentity(identity) && motiveSelected.value && keyReCaptcha && (chassisOrBoard?.match(Regex.chassisOrBoard) || carNew)) {
            setEnableBtn(false);
        } else {
            setEnableBtn(true);
        }
    }, [identity, chassisOrBoard, motiveSelected, keyReCaptcha, carNew]);



    useEffect(() => {
        setLoadingBtn(false);



        if (Array.isArray(dataFinancialPending)) {
            // Quando é array, navegar diretamente para as cobranças
            navigate('pendentes');
        } else if (dataFinancialPending !== undefined && typeof dataFinancialPending === 'object' && dataFinancialPending !== false) {
            // Type assertion para resolver o problema do TypeScript
            const responseData = dataFinancialPending as any;

            // Verificar se existe termo na resposta e se já foi aceito
            if (responseData?.Data?.TermoIturanHub?.TermoIturanHubBase64) {
                // Se o termo já foi aceito (tem data), ir direto para cobranças
                if (responseData.Data.TermoIturanHub.DtAceiteTermo) {
                    setTermoData({
                        TermoIturanHub: responseData.Data.TermoIturanHub,
                        TermoIturanHubBase64: responseData.Data.TermoIturanHub.TermoIturanHubBase64
                    });
                    navigate('pendentes');
                } else {
                    // Se o termo não foi aceito, mostrar o termo
                    setTermoData({
                        TermoIturanHub: responseData.Data.TermoIturanHub,
                        TermoIturanHubBase64: responseData.Data.TermoIturanHub.TermoIturanHubBase64
                    });
                    setShowTermo(true);
                }
            } else if (responseData?.Data?.Cobrancas) {
                // Se tem cobranças mas não tem termo, mostrar cobranças diretamente
                navigate('pendentes');
            } else {
                // Mostrar erro se não for array e não tiver cobranças
                setEnableBtn(false);
                setShowError(true);
                captcha?.current?.reset();
            }
        } else if (dataFinancialPending !== undefined) {
            // Se dataFinancialPending é string (erro) ou false
            setEnableBtn(false);

            // Verificar se é o erro específico VeiculoConectadoPendenteMontadora
            if (typeof dataFinancialPending === 'string' &&
                dataFinancialPending.includes('VeiculoConectadoPendenteMontadoraStellantis')) {
                setShowVeiculoConectadoModal(true);
            } else {
                setShowError(true);
            }

            captcha?.current?.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFinancialPending]);

    return (
        <ContentForgot background={BackgroundLogin} titleNav={isWebView ? "" : (showTermo ? "Termo de Adesão" : "Cobranças em aberto")}>
            <GlobalStyles
                styles={{
                    'iframe[title="Mensagem da empresa"]': {
                        display: 'none !important',
                        opacity: '0 !important'
                    },
                    'iframe[title = "Fechar mensagem"]': {
                        display: 'none !important',
                        opacity: '0 !important'
                    },
                    'iframe[title="Close message"]': {
                        display: 'none !important',
                        opacity: '0 !important'
                    },
                    'iframe[title="Company message"]': {
                        display: 'none !important',
                        opacity: '0 !important'
                    },
                    'iframe[title="Message from company"]': {
                        display: 'none !important',
                        opacity: '0 !important'
                    },
                    '#launcher': {
                        display: 'none !important'
                    }
                }}
            />

            {/* Termo de Adesão */}
            {showTermo && termoData && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '1rem'
                }}>
                    <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        maxWidth: '920px',
                        mx: 'auto'
                        }}>
                        {(pdfLoadError || showPdfBlockedModal) && (
                            <Box sx={{
                                position: 'absolute',
                                top: 8,
                                left: '70%',
                                ml: 8,
                                display: 'flex',
                                alignItems: 'stretch',
                                gap: '0.5rem',
                                px: '0.75rem',
                                py: '0.5rem'
                            }}>
                                <img 
                                    src={IconPdfBlockedTooltip} 
                                    alt="PDF bloqueado" 
                                    style={{ 
                                        width: '1.474rem',
                                        height: '1.866rem',
                                        flexShrink: 0
                                    }} 
                                />
                                <Tooltip
                                    title={
                                        <Box sx={{
                                            width: '12.938rem',
                                            height: '5.375rem'

                                         }}>
                                            <Typography sx={{ 
                                                fontSize: '0.875rem', 
                                                lineHeight: '100%',
                                                color: '#0C1C33',
                                                Spacing: '0.048rem',
                                                textAlign: 'center'
                                            }}>
                                                Visualize o PDF, <strong>permitindo</strong> os pop-ups deste site ou <strong>clique no ícone</strong> de bloqueio na barra de endereços, depois atualize a página.
                                            </Typography>
                                        </Box>
                                    }
                                    placement="bottom"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                backgroundColor: 'white',
                                                border: '1px solid #0AA8E3',
                                                maxWidth: '20rem',
                                                padding: '1rem',
                                                fontSize: '0.875rem'
                                            }
                                        }
                                    }}
                                >
                                    <Typography sx={{
                                        width: '6.938rem',
                                        height: '1.938rem',
                                        color: '#0C1C33',
                                        fontSize: '0.875rem',
                                        whiteSpace: 'pre-line'
                                    }}>
                                        {'Não foi possível\nvisualizar o PDF'}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        width: {
                            xs: '100%',
                            md: '50rem'
                        },
                        display: 'flex',
                        margin: '0 auto',
                        borderRadius: '10px',
                        flexDirection: 'column',
                        border: {
                            xs: 'none',
                            md: '1.5px solid rgba(12, 28, 51, 0.5)'
                        },
                        backgroundColor: 'white',
                        padding: '1.5rem',
                        marginTop: (pdfLoadError || showPdfBlockedModal) ? { xs: '3.5rem', md: '4rem' } : 0
                    }}>
                        {/* Header */}
                        <Typography fontSize='1.5625rem' marginBottom='1.5rem' fontWeight={700} textAlign="center">
                            Termo de Adesão
                        </Typography>

                        {/* Visualizador de PDF */}
                        <Box sx={{
                            width: '100%',
                            height: '60vh',
                            marginBottom: '1.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative'
                        }}>
                            {pdfLoadError || showPdfBlockedModal ? (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '17.625rem',
                                    height: '3.75rem',
                                    px: '1rem',
                                    gap: '1rem',
                                    overflow: 'hidden'
                                }}>
                                    <img 
                                        src={IconPdfBlocked} 
                                        alt="PDF bloqueado"
                                        style={{ 
                                            width: '2.313rem',
                                            height: '2.929rem'
                                        }} 
                                    />
                                    <Typography sx={{
                                        color: '#D5D7D7',
                                        fontSize: '1.3rem',
                                        whiteSpace: 'pre-line'
                                    }}>
                                        <strong>{'PDF BLOQUEADO\nPELO NAVEGADOR'}</strong>
                                    </Typography>
                                </Box>
                            ) : pdfBlobUrl ? (
                                <iframe
                                    src={pdfBlobUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                    title="Termo de Adesão"
                                    onError={() => {
                                        setPdfLoadError(true);
                                        setShowPdfBlockedModal(true);
                                        setPdfLoadChecked(true);
                                        setPdfBlobUrl(null); 
                                    }}
                                    onLoad={(e) => {
                                        const iframe = e.target as HTMLIFrameElement;
                                        const pdfViewerEnabled = (navigator as any).pdfViewerEnabled;

                                        setTimeout(() => {
                                            if (pdfLoadChecked && !pdfLoadError) {
                                                return;
                                            }
                                            if (typeof pdfViewerEnabled === 'boolean' && pdfViewerEnabled === false) {
                                                setPdfLoadError(true);
                                                setShowPdfBlockedModal(true);
                                                setPdfLoadChecked(true);
                                                return;
                                            }

                                            setPdfLoadChecked(true);
                                            
                                        }, 2500);
                                    }}
                                />
                            ) : (
                                <Typography>Carregando PDF...</Typography>
                            )}
                        </Box>
                        {/* Indicador de tempo */}
                        {!hasScrolledToEnd && !pdfLoadError && !showPdfBlockedModal && (
                            <Box sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                                <Typography fontSize='0.875rem' color='text.secondary'>
                                    Aguarde {10 - termoTimer} segundos para habilitar os botões...
                                </Typography>
                            </Box>
                        )}
                        {/* Texto explicativo */}
                        <Box sx={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <Typography
                                fontSize='0.875rem'
                                color='text.primary'
                                fontWeight={500}
                            >
                                <strong>Para seguir com o pagamento</strong> pedimos que leia os termos e aceite com as condições acima:
                            </Typography>
                        </Box>

                        {/* Botões */}
                        <Box sx={{
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'center',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Button
                                variant="outlined"
                                onClick={handleRejectTermo}
                                disabled={!hasScrolledToEnd || loadingAcceptTerm || pdfLoadError || showPdfBlockedModal}
                                sx={{
                                    minWidth: '150px',
                                    height: '48px',
                                    opacity: (hasScrolledToEnd && !loadingAcceptTerm && !pdfLoadError && !showPdfBlockedModal) ? 1 : 0.5
                                }}
                            >
                                {loadingAcceptTerm ? 'PROCESSANDO...' : 'NÃO CONCORDO'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAcceptTermoLocal}
                                disabled={!hasScrolledToEnd || loadingAcceptTerm || pdfLoadError || showPdfBlockedModal}
                                sx={{
                                    minWidth: '150px',
                                    height: '48px',
                                    opacity: (hasScrolledToEnd && !loadingAcceptTerm && !pdfLoadError && !showPdfBlockedModal) ? 1 : 0.5
                                }}
                            >
                                {loadingAcceptTerm ? 'PROCESSANDO...' : 'LI E CONCORDO'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Formulário de Cobranças - só mostra se não tiver termo ou se o termo foi aceito */}
            {(!showTermo || termoAccepted) && (
                <>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <Box sx={{
                            width: {
                                xs: '100%',
                                md: '24.5rem'
                            },
                            display: 'flex',
                            margin: '0 auto',
                            textAlign: 'center',
                            borderRadius: '10px',
                            alignItems: 'center',
                            flexDirection: 'column',
                            padding: '2.375rem 1.6875rem',
                            border: {
                                xs: 'none',
                                md: '1.5px solid rgba(12, 28, 51, 0.5)'
                            },
                            'button': {
                                width: '100%',
                                marginTop: '.6875rem'
                            }
                        }}>
                            <Typography fontSize='1.5625rem' marginBottom='2rem' fontWeight={700} sx={{ display: { xs: 'none', md: 'flex' } }}>
                                Cobranças em aberto
                            </Typography>

                            <Input
                                name='identity'
                                label='Insira seu CPF ou CNPJ'
                                placeholder='000.000.000-00'
                                type='tel'
                                mask
                                identity
                                textError="Insira um número válido"
                                register={{ ...register("identity", { pattern: Regex.identity }) }} />

                            <Box sx={{ marginBottom: '1.56rem', width: '100%' }}>
                                <Select
                                    label='Motivo'
                                    placeholder='Selecione'
                                    itemSelected={motiveSelected}
                                    listItems={dataListMotive}
                                    onChangeSelected={(e) => setMotiveSelected(e)}
                                />
                            </Box>

                            <Input
                                name='chassisOrBoard'
                                label='Placa ou chassi do carro'
                                placeholder='*aa9*99'
                                type='text'
                                mask
                                identity
                                typeIdentity='plaque'
                                disabled={true}
                                register={{ ...register("chassisOrBoard", { pattern: Regex.chassisOrBoard }) }}
                                uppercase
                            />

                            <label style={{ margin: '.2rem 0 .5rem' }}>
                                <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', gap: '.5rem' }}>
                                    <Checkbox checked={carNew} sx={{ padding: '0 0 4px' }} onClick={() => setCarNew(!carNew)} />
                                    <Typography fontSize='1rem' fontWeight={600}>
                                        Veículo Zero Km
                                    </Typography>
                                </Box>
                            </label>

                                <Box sx={{ marginBottom: '1.5rem' }}>
                                    <ReCAPTCHA
                                        ref={captcha as React.RefObject<ReCAPTCHA>}
                                        onChange={(e) => e && setKeyReCaptcha(e)}
                                        sitekey={LoadConfig().REACT_APP_SITE_KEY}
                                    />
                                </Box>

                            <ButtonRectangle disabled={enableBtn} loadingBtn={loadingBtn} name="CONTINUAR" onClick={() => handlePostFinancialPendingState()} />
                        </Box>
                    </Box>

                    <Modals open={showError}>
                        <Box sx={{
                            borderRadius: '8px',
                            textAlign: 'center',
                            width: { xs: 'calc(100vw - 2rem)', md: '20rem' },
                        }}>
                            <Box sx={{ padding: '1.5rem 2rem', borderRadius: '8px 8px 0 0' }}>
                                <Typography fontSize='1rem' fontWeight={700} lineHeight='20px'>
                                    Não foi possível concluir o processo no momento.
                                </Typography>
                                <Typography fontSize='1rem' lineHeight='20px' marginTop='.5rem'>
                                    {typeof dataFinancialPending === 'string' ?
                                        dataFinancialPending
                                        :
                                        'Tente novamente mais tarde.'
                                    }
                                </Typography>
                            </Box>
                            <Box sx={{
                                borderRadius: '0 0 8px 8px',
                                backgroundColor: 'primary.main',
                                padding: '2.1875rem 3.81rem 1.875rem',
                                'button': {
                                    width: '100%',
                                    height: '2.5rem',
                                    maxWidth: '15.68rem',
                                }
                            }}>
                                <ButtonRectangle
                                    name='FECHAR'
                                    onClick={() => setShowError(false)}
                                />
                            </Box>
                        </Box>
                    </Modals>

                    <ModalAlertSecurity />
                </>
            )}

            <Modals open={showPdfBlockedModal} handleClick={setShowPdfBlockedModal}>
                <Box sx={{
                    width: '23.375rem',
                    height: '20.125rem',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    padding: '1.5rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        width: '17.625rem',
                        height: '3.75rem',
                        gap: '0.625rem',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                    }}>
                        <img src={IconPdfBlockedModal} alt="Icone de PDF bloqueado" 
                        style={{ 
                            width: '2.313rem',
                            height: '2.929rem'
                        }} />
                        <Typography sx={{
                            color: '#0C1C33',
                            fontSize: '1.5625rem',
                            fontWeight: 700,
                            marginLeft: '0.625rem',
                            lineHeight: '100%',
                            whiteSpace: 'pre-line'
                        }}>
                            {'PDF BLOQUEADO\nPELO NAVEGADOR'}
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: '20.813rem',
                        height: '6.438rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        marginTop: '1.2rem'
                    }}>
                        <Typography sx={{
                            color: '#0C1C33',
                            fontSize: '0.875rem',
                            lineHeight: '100%',
                            lineSpacing: '0.048rem',
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                        }}>
                            {`Visualize o PDF, `}
                            <strong>permitindo</strong>
                            {` os pop-ups deste site ou `}
                            <strong>clique no ícone</strong>
                            {` de bloqueio na barra de endereços.\n`}
                            <br/>
                            {`Depois disso, `}
                            <strong>atualize</strong>
                            {` a página para visualizar o PDF e clique para aceitar as condições.`}
                        </Typography>
                    </Box>
                    <Box sx={{
                        mt: 'auto',
                        width: '23.375rem',
                        height: '5.56rem',
                        backgroundColor: '#0C1C33',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        ml: '-1.5rem',
                        mr: '-1.5rem',
                        mb: '-1.5rem',
                    }}>                        
                    <Button
                            onClick={() => setShowPdfBlockedModal(false)}
                            sx={{
                                width: '15.688rem',
                                height: '2.5rem',
                                backgroundColor: '#97C93D',
                                padding: '0.125rem',
                                color: '#0C1C33',
                                fontSize: '1rem',
                                fontWeight: '100%',
                                textTransform: 'uppercase',
                                borderRadius: '0.5rem',
                                '&:hover': {
                                  bgcolor: '#97C93D',
                                  color: '#0C1C33',
                                  boxShadow: 'none'
                                },
                            
                            }}
                        >
                            <strong>ENTENDI</strong>
                        </Button>
                    </Box>
                </Box>
            </Modals>

            {/* Modal personalizado para VeiculoConectadoPendenteMontadora */}
            <Modals open={showVeiculoConectadoModal}>
                <Box sx={{
                    width: { xs: 'calc(100vw - 2rem)', md: '400px' },
                    maxWidth: '400px',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}>
                    {/* Seção superior */}
                    <Box sx={{
                        padding: '24px 20px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa'
                    }}>
                        {/* Logo e título */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                            <Typography sx={{ fontSize: '24px', marginRight: '12px' }}>📢</Typography>
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography sx={{
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: '#2d3748',
                                    lineHeight: 1.2
                                }}>
                                    SEGURO CONECTADO
                                </Typography>
                                <Typography sx={{
                                    fontSize: '14px',
                                    color: '#666',
                                    fontWeight: 500
                                }}>
                                    BY ITURAN
                                </Typography>
                            </Box>
                        </Box>

                        {/* Texto principal */}
                        <Typography sx={{
                            fontSize: '16px',
                            color: '#4a5568',
                            marginBottom: '8px',
                            fontWeight: 500
                        }}>
                            Desconto exclusivo no seguro?
                        </Typography>

                        <Typography sx={{
                            fontSize: '20px',
                            color: '#00bcd4',
                            fontWeight: 700,
                            marginBottom: '16px'
                        }}>
                            Só com o Carro Conectado!
                        </Typography>

                        <Typography sx={{
                            fontSize: '14px',
                            color: '#4a5568',
                            lineHeight: 1.4,
                            marginBottom: '20px'
                        }}>
                            Ative os serviços conectados do seu veículo e garanta <strong>condições exclusivas na contratação do seguro*</strong>.
                        </Typography>

                        {/* Lista de passos */}
                        <Box sx={{ textAlign: 'left', marginBottom: '16px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <Typography sx={{ color: '#4caf50', fontSize: '16px', marginRight: '8px' }}>✅</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', fontWeight: 600 }}>
                                    Para ativar é simples:
                                </Typography>
                            </Box>

                            <Box sx={{ paddingLeft: '24px', marginBottom: '12px' }}>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', marginBottom: '4px' }}>
                                    1. Fale com sua concessionária ou<br />pelo <strong>0800 007 7128</strong>;
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', marginBottom: '4px' }}>
                                    2. Complete seu cadastro e defina suas credenciais de acesso;
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>
                                    3. Aceite os Termos e Condições dos serviços conectados.
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <Typography sx={{ color: '#ff9800', fontSize: '16px', marginRight: '8px' }}>👉</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', fontWeight: 600 }}>
                                    Depois disso:
                                </Typography>
                            </Box>

                            <Box sx={{ paddingLeft: '24px', marginBottom: '12px' }}>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', marginBottom: '4px' }}>
                                    1. Finalize a contratação do seguro*;
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>
                                    2. Pague a 1ª mensalidade.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Texto final */}
                        <Typography sx={{
                            fontSize: '14px',
                            color: '#4a5568',
                            fontWeight: 400,
                            lineHeight: 1.4,
                            textAlign: 'center',
                            marginBottom: '8px'
                        }}>
                            <strong>Pronto!</strong> Desconto garantido e cobertura ativada! <strong>Simples, rápido e com desconto!</strong>
                        </Typography>

                        <Typography sx={{
                            fontSize: '11px',
                            color: '#666',
                            textAlign: 'center',
                            marginBottom: '16px'
                        }}>
                            * Seguro oferecido pelas seguradoras parceiras da Ituran.
                        </Typography>
                    </Box>

                    {/* Seção inferior com botão */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, #1a2332 0%, #0f1419 100%)',
                        padding: '24px 20px',
                        textAlign: 'center'
                    }}>
                        <Button
                            onClick={() => setShowVeiculoConectadoModal(false)}
                            sx={{
                                width: '100%',
                                maxWidth: '280px',
                                height: '48px',
                                background: 'transparent',
                                border: '2px solid white',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 600,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                borderRadius: '8px',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            FECHAR
                        </Button>
                    </Box>
                </Box>
            </Modals>
        </ContentForgot>
    );
}

export default Charges;